// Průzkumník souborů ve stylu Windows 10: ribbon, adresní řádek s drobečky, strom vlevo, seznam vpravo, koš,
// přetahování (nahrání i přesun), přejmenování F2, Delete, živá aktualizace složky.
import { registerComponent } from '@core/views/factory';
import { core, action, upload, ApiError } from '@core/api';
import { h, clear, debounce } from '@core/dom';
import { t, tk, fmtDateTime, fmtSize } from '@core/i18n';
import { icon, fileIcon } from '@core/icons';
import { events } from '@core/events';
import { vwindow } from '@core/components/virtual';
import { mimeLabel } from '@core/mime';
import { IwRibbon, type RibbonButton } from '@core/components/ribbon';
import { IwCommandBar, headButton, type BarItem } from '@core/components/commandbar';
import { useBar, renderBarHeader, fabButton, renderSelBar, HeadSearch } from '@core/components/chrome';
import { IwAddressBar } from '@core/components/addressbar';
import { pickFolder } from '@core/shell/picker';
import { savePref } from '@core/theme';
import { downloadItem } from '@core/shell/download';
import { IwSplit, IwTree, type TreeNode, statusBar, emptyState } from '@core/components/misc';
import { IwMenu, contextMenu, type MenuItem } from '@core/components/menu';
import { confirm, prompt } from '@core/components/dialog';
import { toast, toastAction, toastError } from '@core/components/toast';
import { collectFromDrop, pickNative, dropHighlight, runExport } from '@core/shell/dropzone';
import { uploadFiles } from '@core/shell/uploads';
import { fileClipboard, setFileClipboard, pasteFileClipboard, onFileClipboard } from '@core/shell/clipboard';
import { SHARED_ROOT, canWrite, canManage, type StorageItem, type Shortcut } from '@core/contract';
import { loadLabels, labelColor, type CoreLabel } from '@core/labels';

interface Payload {
  roots: { user: StorageItem; shared: StorageItem; desktop: StorageItem };
  start: string;
  /** položka, kterou po načtení označit (hledání podle štítku) */
  select?: string | null;
}

const TRASH = '__trash__';

registerComponent('soubory-explorer', ({ desc, win, wm }) => {
  const payload = desc.payload as unknown as Payload;
  const prefix = (document.getElementById('iwan-boot') && (JSON.parse(document.getElementById('iwan-boot')?.textContent ?? '{}') as { prefix?: string }).prefix) || '';
  let folderId = payload.start;
  let items: StorageItem[] = [];
  let path: { id: string; name: string }[] = [];
  let folderAccess: StorageItem['access'] | undefined; // přístup k aktuální složce (null/undefined = virtuální pohled)
  let selected = new Set<string>();
  let pendingSelect = payload.select ?? null;
  // globální štítky (jádro): barvy teček u názvů, nabídka Štítky
  let labelList: CoreLabel[] = [];
  const refreshLabels = (fresh = false): void => { void loadLabels(fresh).then((l) => { labelList = l; if (items.length) { renderList(); detailKey = ''; detailSoon(); } }); };
  refreshLabels(true);
  const offLabels = events.subscribe(['core.labels'], () => refreshLabels(true), {}); // nový / přejmenovaný štítek
  const prefs0 = (window as unknown as { iwanPrefs?: Record<string, unknown> }).iwanPrefs ?? {};
  let view: 'list' | 'tiles' = prefs0['soubory.view'] === 'tiles' ? 'tiles' : 'list'; // volba se pamatuje (vzor Kontakty)
  // podokno podrobností vpravo (vzor Kontakty – bod D1; jako v Průzkumníku Windows): volba v ⚙ se pamatuje, ukáže se
  // jen na PC v okně širokém aspoň 860 px (telefon otevírá klepnutím)
  let detailPref = prefs0['soubory.detail'] !== false;
  let detailOn = false;
  // řazení (⚙ Řadit podle nebo klepnutí na hlavičku sloupce); složky vždy první jako v Průzkumníku
  type SortCol = 'name' | 'modified' | 'type' | 'size';
  const sortPref = (prefs0['soubory.sort'] && typeof prefs0['soubory.sort'] === 'object' ? prefs0['soubory.sort'] : {}) as { col?: string; dir?: string };
  let sortCol: SortCol = (['name', 'modified', 'type', 'size'] as const).find((x) => x === sortPref.col) ?? 'name';
  let sortDir: 'asc' | 'desc' = sortPref.dir === 'desc' ? 'desc' : 'asc';
  let selectMode = false; // „Vybrat položky“ zapíná uživatel (karta Zobrazení) – i na dotyku výchozí vypnuto (rozhodnutí 25. 10. 2026)
  const history: string[] = [];
  const forward: string[] = [];
  let unsubscribe: (() => void) | null = null;

  const ribbon = new IwRibbon();
  const tree = new IwTree();
  const split = new IwSplit();
  const listEl = h('div', { class: 'files-list', tabindex: '0' });
  const address = new IwAddressBar();
  const status = h('div');
  const searchInput = h('input', { class: 'input search', type: 'search', placeholder: t('soubory.search_here'), 'aria-label': t('soubory.search_here') });
  let searchTerm = '';
  searchInput.addEventListener('input', debounce(() => {
    searchTerm = searchInput.value.trim().toLowerCase();
    renderList();
  }, 250));
  // koncepce A (postup 69): záhlaví ☰ · Soubory · + Nový ▾ · Nahrát ▾ · akce nad výběrem · ⋯ · hledání · ⚙; adresní řádek pod ním
  const bar = useBar() ? new IwCommandBar() : null;
  const fab = fabButton();
  const searchWrap = h('div', { class: 'search-wrap' }, icon('search'), searchInput);
  if (!bar) ribbon.right().appendChild(searchWrap);
  // nové ovládání: hledání v záhlaví jako všude (telefon 🔍 → pole přes záhlaví, zpět ho zavře)
  const hs = bar ? new HeadSearch((q) => { searchTerm = q.toLowerCase(); renderList(); }, t('soubory.search_here')) : null;
  const selBarEl = h('div', { class: 'sel-bar', hidden: true });
  let cursorId: string | null = null; // klávesnice: aktuální položka
  split.first.appendChild(tree);
  const detailEl = h('div', { class: 'files-detail' });
  const dsplit = useBar() ? new IwSplit() : null;
  if (dsplit) { // seznam vlevo, podrobnosti vpravo; šířku podokna mění táhlo a pamatuje se
    dsplit.fixedSecond = true;
    dsplit.classList.add('files-detail-split', 'detail-off');
    dsplit.first.appendChild(listEl);
    dsplit.second.appendChild(detailEl);
    dsplit.setWidth(Number(prefs0['soubory.detailw']) || 340);
    dsplit.defaultSize = 340;
    dsplit.onResize = (px) => void savePref('soubory.detailw', Math.round(px));
    split.second.append(dsplit, status);
  } else split.second.append(listEl, status);
  split.setWidth(220);
  // adresní řádek jde přes celou šířku okna (tlačítko Strom sedí nad sloupcem stromu)
  const el = bar ? h('div', { class: 'view files-view chrome-bar' }, address, split, selBarEl, fab) : h('div', { class: 'view files-view' }, ribbon, address, split);
  if (bar) { // vysunutý strom (úzké okno): klepnutí vedle ho zavře
    const backdrop = h('div', { class: 'side-backdrop' });
    backdrop.addEventListener('click', () => setTree(true, false));
    split.second.appendChild(backdrop);
  }
  if (bar) bar.globalLabel = t('soubory.name');
  let menuAt = 0;
  let backSel = 0;
  // strom složek jde schovat (tlačítko Strom, Ctrl+B). Rozhoduje šířka OKNA, ne zařízení: okno užší než 700 px
  // má strom standardně schovaný a vysouvá ho přes seznam; stav se pamatuje zvlášť pro úzké a široké okno.
  const NARROW = 700;
  const prefs = (window as unknown as { iwanPrefs?: Record<string, unknown> }).iwanPrefs ?? {};
  let narrow: boolean | null = null;
  let treeHidden = false;
  const treePrefKey = () => (narrow ? 'soubory.treeHidden.narrow' : 'soubory.treeHidden.wide');
  let backTree = 0;
  function setTree(hidden: boolean, remember = true): void {
    treeHidden = hidden;
    split.setFirstHidden(hidden);
    el.classList.toggle('side-open', !hidden && Boolean(narrow));
    address.setTreeShown(!hidden);
    if (remember) void savePref(treePrefKey(), hidden);
    // vysunutý strom (úzké okno, telefon): zpět ho zavře
    if (!hidden && narrow && !wm.hasBack(backTree)) backTree = wm.pushBack(() => { backTree = 0; setTree(true, false); return true; }, win);
    if (hidden && backTree) {
      const id = backTree;
      backTree = 0;
      wm.dropBack(id);
    }
  }
  function applyLayout(width: number): void {
    const n = width < NARROW;
    if (n === narrow) return;
    narrow = n;
    split.setOverlay(n);
    const saved = prefs[treePrefKey()];
    setTree(saved === undefined ? n : saved === true, false);
  }
  const layoutObserver = new ResizeObserver(() => { if (el.clientWidth) { applyLayout(el.clientWidth); updateDetailOn(); } });
  layoutObserver.observe(el);
  if (!bar) address.onTreeToggle(() => setTree(!treeHidden)); // v novém ovládání je strom pod ☰ v záhlaví
  const goBack = () => {
    const prev = history.pop();
    if (prev) {
      forward.push(folderId);
      void navigate(prev, false);
    }
  };
  const goForward = () => {
    const next = forward.pop();
    if (next) {
      history.push(folderId);
      void navigate(next, false);
    }
  };
  const goUp = () => {
    const parent = path[path.length - 2];
    if (parent) void navigate(parent.id);
  };

  // ---- strom
  const rootNodes: TreeNode[] = [
    { id: payload.roots.user.id, label: t('soubory.my'), icon: 'home', children: payload.roots.user.hasFolders === false ? [] : null },
    { id: SHARED_ROOT, label: t('soubory.shared_me'), icon: 'users', children: null },
    { id: payload.roots.shared.id, label: t('soubory.shared'), icon: 'building', children: payload.roots.shared.hasFolders === false ? [] : null },
    { id: TRASH, label: t('soubory.trash'), icon: 'trash', children: [] },
  ];
  tree.setNodes(rootNodes);
  tree.onExpand = async (node) => {
    const r = node.id === SHARED_ROOT ? await core<{ items: StorageItem[] }>('storage', 'sharedWithMe') : await core<{ items: StorageItem[] }>('storage', 'children', { id: node.id });
    return r.items.filter((i) => i.kind === 'folder').map((i) => ({ id: i.id, label: i.name, icon: 'folder', children: i.hasFolders ? null : [] }));
  };
  tree.onSelect = (node) => {
    void navigate(node.id);
    if (narrow) setTree(true, false);
  };
  tree.onDrop = (node, e) => void dropOn(node.id, e);

  // ---- ribbon
  const buttons = (): RibbonButton[] => {
    const inTrash = folderId === TRASH;
    const inShared = folderId === SHARED_ROOT;
    const sel = selectedItems();
    const b: RibbonButton[] = [];
    const writable = canWrite(folderAccess);
    const selWritable = sel.length > 0 && sel.every((i) => canWrite(i.access));
    if (inShared) {
      b.push({ id: 'open', label: t('soubory.open'), icon: 'folder-open', tab: 'home', group: 'core.grp.open', primary: true, disabled: sel.length !== 1, onClick: () => sel[0] && void openItem(sel[0]) });
      b.push({ id: 'download', label: t('soubory.download'), icon: 'download', tab: 'home', group: 'core.grp.open', disabled: sel.length !== 1 || sel[0]?.kind !== 'file', onClick: () => sel[0] && void downloadItem(sel[0], prefix, true) });
      b.push({ id: 'props', label: t('soubory.properties'), icon: 'info', tab: 'home', group: 'core.grp.organize', disabled: sel.length !== 1, onClick: () => sel[0] && void wm.open({ app: 'desktop', window: 'vlastnosti', params: { id: sel[0].id }, modal: true, parent: win }) });
      b.push({ id: 'unshare_me', label: t('soubory.remove_shared'), icon: 'x', tab: 'home', group: 'core.grp.organize', danger: true, disabled: !sel.length, onClick: () => void removeShared(sel) });
    } else if (!inTrash) {
      // Domů: Schránka · Uspořádat · Nový · Otevřít · Vybrat (jako Průzkumník Windows 10)
      b.push({ id: 'new_folder', label: t('soubory.new_folder'), icon: 'folder', tab: 'home', group: 'core.grp.new', primary: true, disabled: !writable, onClick: () => void newFolder() });
      b.push({ id: 'new_text', label: t('desktop.shell.new_text'), icon: 'file-text', tab: 'home', group: 'core.grp.new', disabled: !writable, onClick: () => void newText() });
      // Nahrát: vlastní karta (později Sdílet)
      b.push({ id: 'upload', label: t('desktop.shell.upload'), icon: 'upload', tab: 'upload', group: 'soubory.grp_upload', primary: true, disabled: !writable, onClick: () => pickUpload(false) });
      b.push({ id: 'upload_folder', label: t('soubory.upload_folder'), icon: 'folder', tab: 'upload', group: 'soubory.grp_upload', disabled: !writable, onClick: () => pickUpload(true) });
      b.push({ id: 'cut', label: t('soubory.cut'), icon: 'scissors', shortcut: 'Ctrl+X', tab: 'home', group: 'core.grp.clipboard', disabled: !selWritable, onClick: () => setFileClipboard(sel.map((i) => i.id), true) });
      b.push({ id: 'copy', label: t('soubory.copy'), icon: 'copy', shortcut: 'Ctrl+C', tab: 'home', group: 'core.grp.clipboard', disabled: !sel.length, onClick: () => setFileClipboard(sel.map((i) => i.id), false) });
      b.push({ id: 'paste', label: t('soubory.paste'), icon: 'clipboard', shortcut: 'Ctrl+V', tab: 'home', group: 'core.grp.clipboard', disabled: !fileClipboard() || !writable, onClick: () => void paste() });
      b.push({ id: 'open', label: t('soubory.open'), icon: 'folder-open', tab: 'home', group: 'core.grp.open', disabled: sel.length !== 1, onClick: () => sel[0] && void openItem(sel[0]) });
      b.push({ id: 'download', label: t('soubory.download'), icon: 'download', tab: 'home', group: 'core.grp.open', disabled: sel.length !== 1 || sel[0]?.kind !== 'file', onClick: () => sel[0] && void downloadItem(sel[0], prefix, true) });
      b.push({ id: 'rename', label: t('soubory.rename'), icon: 'edit', shortcut: 'F2', tab: 'home', group: 'core.grp.organize', disabled: sel.length !== 1 || !selWritable, onClick: () => sel[0] && void rename(sel[0]) });
      b.push({ id: 'move', label: t('soubory.move_to'), icon: 'folder-open', tab: 'home', group: 'core.grp.organize', disabled: !selWritable, onClick: () => void moveTo(sel) });
      b.push({ id: 'delete', label: t('soubory.delete'), icon: 'trash', shortcut: 'Delete', tab: 'home', group: 'core.grp.organize', danger: true, disabled: !selWritable, onClick: () => void trash(sel) });
      b.push({ id: 'labels', label: t('soubory.labels'), icon: 'tag', tab: 'home', group: 'core.grp.organize', disabled: !taggable(sel), menu: tagMenu(sel) });
      b.push({ id: 'share', label: t('soubory.share'), icon: 'users', tab: 'home', group: 'core.grp.organize', disabled: sel.length !== 1 || !sharable(sel[0]), onClick: () => sel[0] && shareDialog(sel[0]) });
      b.push({ id: 'props', label: t('soubory.properties'), icon: 'info', tab: 'home', group: 'core.grp.organize', disabled: sel.length !== 1, onClick: () => sel[0] && void wm.open({ app: 'desktop', window: 'vlastnosti', params: { id: sel[0].id }, modal: true, parent: win }) });
      b.push({ id: 'select_all', label: t('core.cmd.select_all'), icon: 'check-square', shortcut: 'Ctrl+A', tab: 'home', group: 'soubory.grp_select', disabled: !items.length, onClick: () => selectAll() });
      b.push({ id: 'select_none', label: t('soubory.select_none'), icon: 'x', tab: 'home', group: 'soubory.grp_select', disabled: !sel.length, onClick: () => clearSelection() });
      b.push({ id: 'select_mode', label: t('core.ui.select_mode'), icon: 'check-square', tab: 'view', group: 'core.grp.view', toggle: true, active: selectMode, onClick: () => setSelectMode(!selectMode) });
    } else {
      b.push({ id: 'restore', label: t('soubory.restore'), icon: 'rotate', tab: 'home', group: 'core.grp.organize', primary: true, disabled: !sel.length, onClick: () => void restore(sel) });
      b.push({ id: 'purge', label: t('soubory.purge'), icon: 'trash', tab: 'home', group: 'core.grp.organize', danger: true, disabled: !sel.length, onClick: () => void purge(sel) });
      b.push({ id: 'empty', label: t('soubory.empty_trash_cmd'), icon: 'trash', tab: 'home', group: 'core.grp.manage', danger: true, onClick: () => void emptyTrash() });
    }
    b.push({ id: 'view_list', label: bar ? t('core.ui.view_table') : t('soubory.view_list'), icon: bar ? 'table' : 'list', tab: 'view', group: 'core.grp.view', toggle: true, active: view === 'list', onClick: () => setView('list') });
    b.push({ id: 'view_tiles', label: t('soubory.view_tiles'), icon: 'tiles', tab: 'view', group: 'core.grp.view', toggle: true, active: view === 'tiles', onClick: () => setView('tiles') });
    if (dsplit && wm.currentMode() !== 'mobile') b.push({ id: 'detail_pane', label: t('soubory.detail_pane'), icon: 'layout-right', tab: 'view', group: 'core.grp.view', toggle: true, active: detailPref, onClick: () => setDetailPref(!detailPref) });
    b.push({ id: 'refresh', label: t('core.cmd.refresh'), icon: 'refresh', shortcut: 'F5', tab: 'view', group: 'core.grp.view', onClick: () => void load() });
    return b;
  };
  /** Položky lišty z tlačítek pásu karet: Nový ▾ (hlavní), Nahrát ▾, akce nad výběrem, Vložit, ⋯, zobrazení pod ⚙. */
  function barItems(): BarItem[] {
    const all = buttons();
    const by = (id: string): RibbonButton | undefined => all.find((b) => b.id === id);
    const item = (b: RibbonButton | undefined, place: BarItem['place'], extra: Partial<BarItem> = {}): BarItem[] => (b ? [{ id: b.id, label: b.label, icon: b.icon, shortcut: b.shortcut, disabled: b.disabled, danger: b.danger, toggle: b.toggle, active: b.active, place, onClick: b.onClick, ...extra }] : []);
    const menuOf = (ids: string[]) => ids.map(by).filter((b): b is RibbonButton => Boolean(b) && !b?.disabled).map((b) => ({ id: b.id, label: b.label, icon: b.icon ?? null, onSelect: () => b.onClick?.() }));
    const out: BarItem[] = [];
    const news = menuOf(['new_folder', 'new_text']);
    if (by('new_folder') && news.length) out.push({ id: 'new', label: t('core.cmd.new'), icon: 'plus', place: 'primary', menu: news });
    const ups = menuOf(['upload', 'upload_folder']);
    if (by('upload') && ups.length) out.push({ id: 'upload_menu', label: t('soubory.upload_short'), icon: 'upload', place: 'action', menu: ups });
    for (const id of ['open', 'download', 'restore', 'cut', 'copy', 'move', 'rename', 'delete', 'purge', 'unshare_me']) out.push(...item(by(id), 'selection'));
    const lb = by('labels');
    if (lb && !lb.disabled) out.push({ id: 'labels', label: lb.label, icon: lb.icon, place: 'more', scope: 'selection', menu: tagMenu(selectedItems()) });
    for (const id of ['share', 'props']) out.push(...item(by(id), 'more', { scope: 'selection' }));
    if (!by('paste')?.disabled) out.push(...item(by('paste'), 'action')); // Vložit jen když je co (neaktivní se neschovává šedě)
    if (items.length) out.push(...item(by('empty'), 'action'));
    out.push(...item(by('select_all'), 'more'), ...item(by('select_none'), 'more'), ...item(by('refresh'), 'more'));
    for (const id of ['view_list', 'view_tiles', 'detail_pane']) out.push(...item(by(id), 'view'));
    out.push({ id: 'sort', label: t('core.ui.sort_by'), icon: 'sort', place: 'view', menu: sortMenu() });
    out.push(...item(by('select_mode'), 'view'));
    return out;
  }
  function renderHeader(): void {
    if (!bar) return;
    const selecting = selectMode && wm.currentMode() === 'mobile';
    renderBarHeader({
      win, wm, bar, fab, search: hs,
      menu: { label: t('soubory.tree_toggle'), onClick: () => setTree(!treeHidden, !narrow) },
      select: selecting ? { count: selected.size, onCancel: () => setSelectMode(false), onAll: () => selectAll() } : null,
    });
    renderSelBar(selBarEl, bar.all(), selecting && selected.size > 0);
  }
  /** Režim výběru (telefon: dlouhý stisk; ⚙ Vybrat položky): klepnutí přidává / ubírá, zpět ho ukončí (jako Kontakty). */
  function setSelectMode(on: boolean): void {
    if (on && !wm.hasBack(backSel)) backSel = wm.pushBack(() => { backSel = 0; setSelectMode(false); return true; }, win);
    if (!on && backSel) {
      const id = backSel;
      backSel = 0;
      wm.dropBack(id);
    }
    selectMode = on;
    if (!on) selected = new Set();
    listEl.classList.toggle('select-mode', on);
    renderList();
    refreshRibbon();
  }
  function sortMenu(): MenuItem[] {
    const cols: SortCol[] = ['name', 'modified', 'type', 'size'];
    const lbl: Record<SortCol, string> = { name: t('soubory.name_col'), modified: t('soubory.modified'), type: t('soubory.type'), size: t('soubory.size') };
    return [
      ...cols.map((c): MenuItem => ({ label: lbl[c], checked: sortCol === c, onSelect: () => setSort(c, sortCol === c ? sortDir : c === 'modified' || c === 'size' ? 'desc' : 'asc') })),
      { separator: true, label: '' },
      { label: t('core.ui.sort_asc'), checked: sortDir === 'asc', onSelect: () => setSort(sortCol, 'asc') },
      { label: t('core.ui.sort_desc'), checked: sortDir === 'desc', onSelect: () => setSort(sortCol, 'desc') },
    ];
  }
  function setSort(col: SortCol, dir: 'asc' | 'desc'): void {
    sortCol = col;
    sortDir = dir;
    void savePref('soubory.sort', { col, dir });
    renderList();
    refreshRibbon();
  }
  /** Zobrazené položky: hledání + řazení (složky první). */
  function shownItems(): StorageItem[] {
    const list = searchTerm ? items.filter((i) => i.name.toLowerCase().includes(searchTerm)) : [...items];
    const coll = new Intl.Collator('cs', { numeric: true, sensitivity: 'base' });
    const val = (i: StorageItem): string | number => (sortCol === 'size' ? i.size ?? 0 : sortCol === 'modified' ? i.updatedAt ?? i.createdAt ?? '' : sortCol === 'type' ? typeLabel(i) : i.name);
    list.sort((a, b) => {
      if ((a.kind === 'folder') !== (b.kind === 'folder')) return a.kind === 'folder' ? -1 : 1;
      const x = val(a);
      const y = val(b);
      const c = typeof x === 'number' && typeof y === 'number' ? x - y : coll.compare(String(x), String(y));
      return (sortDir === 'asc' ? c : -c) || coll.compare(a.name, b.name);
    });
    return list;
  }
  const refreshRibbon = () => {
    if (!bar) {
      ribbon.setButtons(buttons());
      return;
    }
    bar.setItems(barItems());
    renderHeader();
  };
  ribbon.setTabLabels({ upload: 'soubory.tab_upload' });
  ribbon.setMode(wm.currentMode());
  function selectAll(): void {
    selected = new Set(items.map((i) => i.id));
    paintSelection();
    refreshRibbon();
    renderStatus();
  }
  function clearSelection(): void {
    selected = new Set();
    paintSelection();
    refreshRibbon();
    renderStatus();
  }

  // ---- navigace
  const navigate = async (id: string, record = true) => {
    if (record && folderId !== id) {
      history.push(folderId);
      forward.length = 0;
    }
    folderId = id;
    selected = new Set();
    cursorId = null;
    if (hs && hs.value()) { hs.set(''); searchTerm = ''; } // jiná složka: hledání od začátku (jako Průzkumník)
    unsubscribe?.();
    unsubscribe = id === TRASH ? null : events.subscribe([id === SHARED_ROOT ? 'core.shares' : 'core.storage:' + id], () => reload(), { onReset: () => reload() });
    tree.select(id);
    await load();
  };
  const reload = debounce(() => void load(), 250);

  async function load(): Promise<void> {
    try {
      if (folderId === TRASH) {
        const r = await core<{ items: StorageItem[] }>('storage', 'trashList');
        items = r.items;
        path = [{ id: TRASH, name: t('soubory.trash') }];
        folderAccess = undefined;
      } else if (folderId === SHARED_ROOT) {
        const r = await core<{ items: StorageItem[] }>('storage', 'sharedWithMe');
        items = r.items;
        path = [{ id: SHARED_ROOT, name: t('soubory.shared_me') }];
        folderAccess = undefined;
      } else {
        const r = await core<{ folder: StorageItem; items: StorageItem[]; path: { id: string; name: string }[] }>('storage', 'children', { id: folderId });
        items = r.items;
        folderAccess = r.folder.access;
        path = r.path.map((p, i) => ({ id: p.id, name: i === 0 ? (p.id === payload.roots.user.id ? t('soubory.my') : p.id === payload.roots.shared.id ? t('soubory.shared') : p.id === SHARED_ROOT ? t('soubory.shared_me') : p.name) : p.name }));
      }
      // strom odpovídá výpisu: podsložky aktuální složky, šipka jen kde jsou další podsložky
      if (folderId !== TRASH) {
        tree.setChildren(folderId, items.filter((i) => i.kind === 'folder').map((i) => ({ id: i.id, label: i.name, icon: 'folder', children: i.hasFolders ? null : [] })));
        void tree.expandPath(path.map((p) => p.id)).then(() => tree.select(folderId));
      }
      if (!bar) {
        searchTerm = '';
        searchInput.value = '';
      }
      if (pendingSelect && items.some((i) => i.id === pendingSelect)) { selected = new Set([pendingSelect]); cursorId = pendingSelect; }
      pendingSelect = null;
      selected = new Set(Array.from(selected).filter((id) => items.some((i) => i.id === id))); // smazané / přesunuté pryč z výběru
      renderCrumbs();
      renderList();
      refreshRibbon();
      detailSoon();
      win.setTitle(bar ? t('soubory.name') : `${t('soubory.name')} – ${path[path.length - 1]?.name ?? ''}`, 'folder'); // složka je v adresním řádku
    } catch (e) {
      toast(tk((e as ApiError).message, (e as ApiError).params ?? undefined), 'error');
    }
  }

  function renderCrumbs(): void {
    address.setPath(path, (id) => void navigate(id));
    address.setNav({ canBack: history.length > 0, canForward: forward.length > 0, canUp: path.length > 1, onBack: goBack, onForward: goForward, onUp: goUp });
  }

  function selectedItems(): StorageItem[] {
    return items.filter((i) => selected.has(i.id));
  }

  // plynulý seznam (nové ovládání, vzor Kontakty – bod C1): vykreslí se jen okno kolem posunu, i složka s tisíci položek
  // je plynulá; výška řádku / dlaždice se změří z vykreslených a všechny jsou pak stejné
  let shownList: StorageItem[] = [];
  let vLineH = 0;
  let vStart = -1;
  let vEnd = -1;
  let vRaf = 0;
  let vMeasuring = false;
  let vShape = '';
  let vKey = '';
  const vCols = (): number => (view === 'tiles' ? Math.max(1, Math.floor((listEl.clientWidth - 20 + 6) / 116)) : 1);
  /** Nad prvním řádkem: přilepená hlavička tabulky / okraj mřížky dlaždic. */
  const vHeadH = (): number => (view === 'list' ? (listEl.querySelector<HTMLElement>('thead')?.offsetHeight ?? 0) : 10);
  function renderWindow(force: boolean, top = listEl.scrollTop): void {
    const host = listEl.querySelector<HTMLElement>(view === 'list' ? 'tbody' : '.tiles');
    if (!bar || !host) return;
    const cols = vCols();
    const lh = vLineH || (view === 'tiles' ? 122 : wm.currentMode() === 'mobile' ? 45 : 33);
    const w = vwindow(shownList.length, cols, lh, Math.max(0, top - vHeadH()), listEl.clientHeight || 600);
    if (!force && w.start === vStart && w.end === vEnd) return;
    vStart = w.start;
    vEnd = w.end;
    const slice = shownList.slice(w.start, w.end);
    if (view === 'list') {
      const pad = (px: number) => h('tr', { class: 'vpad', 'aria-hidden': 'true' }, h('td', { colspan: '4', style: { height: px + 'px' } }));
      host.replaceChildren(pad(w.padTop), ...slice.map((it) => row(it)), pad(w.padBottom));
    } else {
      host.style.paddingTop = 10 + w.padTop + 'px';
      host.style.paddingBottom = 10 + w.padBottom + 'px';
      host.replaceChildren(...slice.map((it) => tile(it)));
    }
    if (!vMeasuring && !vLineH) {
      let mh = 0;
      host.querySelectorAll<HTMLElement>(view === 'list' ? 'tr[data-id]' : '.tile').forEach((n) => { mh = Math.max(mh, n.offsetHeight); });
      if (mh > 10) {
        vLineH = mh + (view === 'tiles' ? 6 : 0);
        listEl.style.setProperty('--files-row-h', mh + 'px');
        vMeasuring = true;
        try { renderWindow(true, top); } finally { vMeasuring = false; }
      }
    }
  }
  /** Posunout seznam, aby byla položka na pozici i vidět (pod přilepenou hlavičkou tabulky). */
  function scrollToItem(i: number): void {
    const lh = vLineH || 33;
    const head = vHeadH();
    const y = Math.floor(i / vCols()) * lh;
    const sticky = view === 'list' ? 0 : head;
    if (y + sticky < listEl.scrollTop) listEl.scrollTop = y + sticky;
    else if (y + head + lh > listEl.scrollTop + listEl.clientHeight) listEl.scrollTop = y + head + lh - listEl.clientHeight;
    renderWindow(false);
  }
  const scheduleWindow = (force = false): void => { if (!bar) return; cancelAnimationFrame(vRaf); vRaf = requestAnimationFrame(() => renderWindow(force)); };
  listEl.addEventListener('scroll', () => scheduleWindow(), { passive: true });
  new ResizeObserver(() => scheduleWindow(true)).observe(listEl); // jiná šířka = jiný počet dlaždic na řádek

  function renderList(): void {
    // posun zůstane při obnovení téže složky; jiná složka, hledání, řazení nebo zobrazení začne nahoře
    const key = [folderId, searchTerm, sortCol, sortDir, view].join('|');
    const keepTop = key === vKey ? listEl.scrollTop : 0;
    vKey = key;
    clear(listEl);
    listEl.dataset['view'] = view;
    listEl.classList.toggle('select-mode', selectMode);
    const shown = bar ? shownItems() : searchTerm ? items.filter((i) => i.name.toLowerCase().includes(searchTerm)) : items;
    if (!shown.length) {
      const st = emptyState(searchTerm ? t('core.ui.nothing_found', { q: searchTerm }) : folderId === TRASH ? t('soubory.empty_trash') : folderId === SHARED_ROOT ? t('soubory.empty_shared') : t('soubory.empty'), folderId === TRASH ? 'trash' : folderId === SHARED_ROOT ? 'users' : 'folder-open');
      if (bar) { // cesta dál (vzor Kontakty): Vymazat hledání / Nová složka · Nahrát
        const acts: HTMLElement[] = [];
        const btn = (label: string, ic: string, fn: () => void, primary = false) => { const b = h('button', { type: 'button', class: 'btn' + (primary ? ' primary' : '') }, icon(ic), label); b.addEventListener('click', fn); acts.push(b); };
        if (searchTerm && hs) btn(t('core.ui.clear_search'), 'x', () => { hs.set(''); if (hs.open) hs.toggle(false); else { searchTerm = ''; renderList(); } });
        if (folderId !== TRASH && folderId !== SHARED_ROOT && canWrite(folderAccess)) {
          btn(t('soubory.new_folder'), 'folder', () => void newFolder());
          btn(t('soubory.upload_short'), 'upload', () => pickUpload(false), true);
        }
        if (acts.length) st.appendChild(h('div', { class: 'list-empty-actions' }, acts));
      }
      listEl.appendChild(st);
      renderStatus();
      return;
    }
    if (view === 'list') {
      const th = (col: SortCol, cls: string | null, text: string): HTMLElement => {
        const on = bar !== null && sortCol === col;
        const cell = h('th', { class: (cls ?? '') + (bar ? ' sortable' : ''), 'aria-sort': on ? (sortDir === 'asc' ? 'ascending' : 'descending') : null }, text, on ? icon(sortDir === 'asc' ? 'arrow-up' : 'chevron-down', 'icon sort-icon') : null);
        if (bar) cell.addEventListener('click', () => setSort(col, sortCol === col ? (sortDir === 'asc' ? 'desc' : 'asc') : col === 'modified' || col === 'size' ? 'desc' : 'asc'));
        return cell;
      };
      const table = h(
        'table',
        { class: 't files-table' },
        h('thead', null, h('tr', null, th('name', null, t('soubory.name_col')), th('modified', 'w-date', t('soubory.modified')), th('type', 'w-type', folderId === SHARED_ROOT ? t('soubory.shared_by') : t('soubory.type')), th('size', 'w-size right', t('soubory.size')))),
        h('tbody', null, bar ? [] : shown.map((it) => row(it))),
      );
      listEl.appendChild(table);
    } else {
      listEl.appendChild(h('div', { class: 'tiles' }, bar ? [] : shown.map((it) => tile(it))));
    }
    if (bar) {
      shownList = shown;
      const shape = view + '|' + wm.currentMode();
      if (shape !== vShape) { // jiné zobrazení / režim = jiná výška řádku
        vShape = shape;
        vLineH = 0;
        listEl.style.removeProperty('--files-row-h');
      }
      listEl.classList.add('virtual');
      vStart = vEnd = -1;
      renderWindow(true, keepTop);
      listEl.scrollTop = keepTop;
    }
    renderStatus();
  }

  /** Štítky položky jako barevné tečky za názvem (název v bublině). */
  function tagDots(it: StorageItem): HTMLElement | null {
    const tags = it.tags ?? [];
    if (!tags.length) return null;
    return h('span', { class: 'files-tags', title: tags.join(', ') }, tags.slice(0, 5).map((n) => h('span', { class: 'files-tag-dot', style: { '--tag': labelColor(labelList, n) ?? '#888' } })));
  }
  /** Štítek přidat / odebrat vybraným (má-li ho každý z výběru, odebere se). */
  async function toggleTag(sel: StorageItem[], name: string): Promise<void> {
    const low = name.toLowerCase();
    const allHave = sel.every((i) => (i.tags ?? []).some((x) => x.toLowerCase() === low));
    try {
      await core('storage', 'tags', { ids: sel.map((i) => i.id), add: allHave ? [] : [name], remove: allHave ? [name] : [] });
      for (const i of sel) i.tags = allHave ? (i.tags ?? []).filter((x) => x.toLowerCase() !== low) : Array.from(new Set([...(i.tags ?? []), name]));
      renderList();
      detailKey = '';
      detailSoon();
    } catch (e) { toast(tk((e as ApiError).message, (e as ApiError).params ?? undefined), 'error'); }
  }
  /** Nabídka Štítky pro výběr: zaškrtnuté = mají všechny; dole Spravovat štítky a Zobrazit označené. */
  function tagMenu(sel: StorageItem[]): MenuItem[] {
    const has = (n: string): boolean => sel.length > 0 && sel.every((i) => (i.tags ?? []).some((x) => x.toLowerCase() === n.toLowerCase()));
    return [
      ...labelList.map((l): MenuItem => ({ label: l.name, color: l.color, checked: has(l.name), onSelect: () => void toggleTag(sel, l.name) })),
      { separator: true, label: '' },
      { label: t('core.labels.manage'), icon: 'settings', onSelect: () => void wm.open({ app: 'desktop', window: 'stitky' }) },
    ];
  }
  const taggable = (sel: StorageItem[]): boolean => sel.length > 0 && sel.every((i) => (i.space === 'user' || i.space === 'shared') && canWrite(i.access));

  /** Sdílet… (lidé ve firmě a odkaz mimo firmu v jednom dialogu): vlastní položka se správou, nebo položka se zápisem (odkaz). */
  function sharable(it: StorageItem | undefined): boolean {
    if (!it || it.isShortcut || !it.parentId) return false;
    return (it.space === 'user' && canManage(it.access)) || ((it.space === 'user' || it.space === 'shared') && canWrite(it.access));
  }
  function shareDialog(it: StorageItem): void {
    // po zavření dialogu obnovit podokno (počty odkazů, se kým je sdíleno)
    void wm.open({ app: 'desktop', window: 'sdileni', params: { type: 'storage', id: it.id, title: it.name }, modal: true, parent: win }).then((w) => {
      if (!w) return;
      const prev = w.onClose;
      w.onClose = () => { prev?.(); detailKey = ''; detailSoon(); };
    });
  }

  function typeLabel(it: StorageItem): string {
    if (it.kind === 'folder') return t('soubory.folder');
    if (it.isShortcut) return t('soubory.shortcut');
    return mimeLabel(it.mime, it.name); // „Dokument PDF“ místo application/pdf
  }

  function row(it: StorageItem): HTMLElement {
    const info = folderId === SHARED_ROOT ? `${it.sharedByName ?? '—'}${canWrite(it.access) ? '' : ' · ' + t('soubory.read_only')}` : typeLabel(it);
    const tr = h('tr', { class: selected.has(it.id) ? 'selected' : '', dataset: { id: it.id }, draggable: 'true' }, h('td', { class: 'name-cell' }, selectMode ? h('span', { class: 'sel-check' }, icon('check')) : null, icon(fileIcon(it.mime, it.isShortcut, it.kind === 'folder')), h('span', null, it.name), tagDots(it), it.shared && folderId !== SHARED_ROOT ? icon('users', 'icon shared-badge') : null), h('td', null, fmtDateTime(it.updatedAt ?? it.createdAt)), h('td', { class: 'muted', title: it.mime ?? '' }, info), h('td', { class: 'right' }, it.kind === 'file' ? fmtSize(it.size) : ''));
    wire(tr, it);
    return tr;
  }

  function tile(it: StorageItem): HTMLElement {
    const el2 = h('div', { class: 'tile' + (selected.has(it.id) ? ' selected' : ''), dataset: { id: it.id }, draggable: 'true', title: it.name }, selectMode ? h('span', { class: 'sel-check' }, icon('check')) : null, it.kind === 'file' && (it.mime?.startsWith('image/') || it.mime === 'application/pdf') && it.url ? h('img', { src: prefix + it.url + '&w=128', alt: '', loading: 'lazy', onError: (e: Event) => { const img = e.target as HTMLImageElement; img.replaceWith(icon(fileIcon(it.mime, it.isShortcut, false), 'icon big')); } }) : icon(fileIcon(it.mime, it.isShortcut, it.kind === 'folder'), 'icon big'), h('span', { class: 'tile-name' }, it.name));
    wire(el2, it);
    return el2;
  }

  function wire(node: HTMLElement, it: StorageItem): void {
    node.addEventListener('click', (e) => {
      // telefon (nové ovládání): klepnutí otevře složku / soubor, výběr je přes dlouhý stisk / režim výběru
      if (bar && wm.currentMode() === 'mobile' && !selectMode && !e.ctrlKey && !e.metaKey && !e.shiftKey && Date.now() > menuAt + 1200) {
        void openItem(it);
        return;
      }
      if (Date.now() < menuAt + 1200 && bar && wm.currentMode() === 'mobile') return; // dlouhý stisk právě zapnul výběr
      cursorId = it.id;
      if (selectMode || e.ctrlKey || e.metaKey) {
        if (selected.has(it.id)) selected.delete(it.id);
        else selected.add(it.id);
        if (bar && selectMode && !selected.size && wm.currentMode() === 'mobile') { setSelectMode(false); return; }
      } else if (e.shiftKey && selected.size) {
        const ids = (bar ? shownList : items).map((x) => x.id);
        const last = ids.indexOf(Array.from(selected).pop() as string);
        const cur = ids.indexOf(it.id);
        for (let i = Math.min(last, cur); i <= Math.max(last, cur); i++) selected.add(ids[i] as string);
      } else {
        selected = new Set([it.id]);
      }
      paintSelection();
      refreshRibbon();
      renderStatus();
    });
    node.addEventListener('dblclick', () => void openItem(it));
    node.addEventListener('dragstart', (e) => {
      if (!selected.has(it.id)) selected = new Set([it.id]);
      e.dataTransfer?.setData('application/x-iwan-items', JSON.stringify(Array.from(selected)));
      e.dataTransfer?.setData('text/plain', it.name);
      if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
    });
    if (it.kind === 'folder') {
      node.addEventListener('dragover', (e) => {
        e.preventDefault();
        node.classList.add('drop');
      });
      node.addEventListener('dragleave', () => node.classList.remove('drop'));
      node.addEventListener('drop', (e) => {
        node.classList.remove('drop');
        e.stopPropagation();
        void dropOn(it.id, e);
      });
    }
    contextMenu(node, (e) => {
      menuAt = Date.now(); // dlouhý stisk: následné „klepnutí“ položku neotevře
      if (bar && 'pointerType' in e && e.pointerType === 'touch') { // dlouhý stisk = režim výběru (jako Kontakty a Pošta)
        e.preventDefault();
        if (!selectMode) setSelectMode(true);
        selected.add(it.id);
        paintSelection();
        refreshRibbon();
        renderStatus();
        return null;
      }
      if (!selected.has(it.id)) {
        selected = new Set([it.id]);
        paintSelection();
        refreshRibbon();
      }
      return itemMenu(it);
    });
  }

  function paintSelection(): void {
    listEl.querySelectorAll<HTMLElement>('[data-id]').forEach((n) => n.classList.toggle('selected', selected.has(n.dataset['id'] ?? '')));
    detailSoon();
  }

  // ---- podokno podrobností: jeden vybraný = náhled, vlastnosti, sdílení a rychlé akce; víc = „Vybráno N“ a velikost;
  // nic = údaje o složce
  interface Info { item: StorageItem; path: { id: string; name: string }[]; owner: string | null; createdBy: string | null; updatedBy: string | null; access: string | null; canShare: boolean; shares: { name: string; level: string }[] }
  let detailKey = '';
  let detailSeq = 0;
  const detailSoon = debounce(() => void renderDetail(), 120);
  function updateDetailOn(): void {
    if (!dsplit) return;
    const on = detailPref && wm.currentMode() !== 'mobile' && el.clientWidth >= 860;
    if (on === detailOn) return;
    detailOn = on;
    dsplit.classList.toggle('detail-off', !on);
    detailKey = '';
    if (on) detailSoon();
  }
  function setDetailPref(on: boolean): void {
    detailPref = on;
    void savePref('soubory.detail', on);
    updateDetailOn();
    refreshRibbon();
  }
  const sumSize = (list: StorageItem[]): number => list.reduce((a, i) => a + (i.kind === 'file' ? i.size ?? 0 : 0), 0);
  const aboutRow = (label: string, value: string): HTMLElement => h('div', { class: 'about-row' }, h('span', { class: 'muted' }, label), h('span', null, value));
  const quick = (label: string, ic: string, fn: () => void): HTMLElement => {
    const b = h('button', { type: 'button', class: 'quick-act', title: label }, icon(ic), h('span', null, label));
    b.addEventListener('click', fn);
    return b;
  };
  /** Vizitka jako u záznamů jádra: obrázek / náhled, název, podtitul, rychlé akce. */
  const card = (media: Node, title: string, sub: string, acts: HTMLElement[] = []): HTMLElement =>
    h('div', { class: 'record-card files-detail-card' }, h('div', { class: 'files-detail-media' }, media), h('div', { class: 'record-card-title' }, title), sub ? h('div', { class: 'record-card-sub' }, sub) : null, h('div', { class: 'record-card-acts' }, acts));
  /** Náhled podle typu: obrázek / PDF (zmenšenina), video, zvuk, začátek textu; jinak ikona. */
  function previewOf(it: StorageItem): Element {
    const fallback = icon(fileIcon(it.mime, it.isShortcut, it.kind === 'folder'), 'icon huge');
    if (it.kind !== 'file' || it.isShortcut || !it.url) return fallback;
    const src = prefix + it.url;
    const m = it.mime ?? '';
    if (m.startsWith('image/') || m === 'application/pdf') {
      const img = h('img', { class: 'files-detail-img', src: src + '&w=480', alt: '' }) as HTMLImageElement;
      img.addEventListener('error', () => img.replaceWith(fallback));
      return img;
    }
    if (m.startsWith('video/')) return h('video', { class: 'files-detail-video', src, controls: true, preload: 'metadata' });
    if (m.startsWith('audio/')) return h('div', { class: 'files-detail-audio' }, fallback, h('audio', { src, controls: true, preload: 'metadata' }));
    if ((m.startsWith('text/') || m === 'application/json' || m === 'application/xml') && it.size <= 200_000) {
      const pre = h('pre', { class: 'files-detail-text' }, '…');
      void core<{ content: string }>('storage', 'text', { id: it.id }).then((r) => { pre.textContent = r.content.length > 3000 ? r.content.slice(0, 3000) + '\n…' : r.content; }).catch(() => pre.replaceWith(fallback));
      return pre;
    }
    return fallback;
  }
  const rootName = (p: { id: string; name: string }): string => (p.id === payload.roots.user.id ? t('soubory.my') : p.id === payload.roots.shared.id ? t('soubory.shared') : p.id === SHARED_ROOT ? t('soubory.shared_me') : p.name);
  async function renderDetail(): Promise<void> {
    if (!detailOn) return;
    const sel = selectedItems();
    const one = sel.length === 1 ? sel[0] : undefined;
    const key = one ? `${one.id}|${one.updatedAt ?? ''}|${one.version}|${one.name}` : sel.length ? `n${sel.length}|${sumSize(sel)}` : `f|${folderId}|${items.length}|${sumSize(items)}`;
    if (key === detailKey) return;
    detailKey = key;
    const seq = ++detailSeq;
    if (sel.length > 1) {
      const size = sumSize(sel);
      detailEl.replaceChildren(card(icon('check-square', 'icon huge'), t('core.ui.selected', { count: sel.length }), size ? fmtSize(size) : ''));
      return;
    }
    if (!one) { // bez výběru: o otevřené složce
      const size = sumSize(items);
      detailEl.replaceChildren(card(icon(folderId === TRASH ? 'trash' : 'folder-open', 'icon huge'), path[path.length - 1]?.name ?? '', t('soubory.items', { count: items.length }) + (size ? ' · ' + fmtSize(size) : '')), h('p', { class: 'muted files-detail-hint' }, t('soubory.pick_item')));
      return;
    }
    const acts: HTMLElement[] = [];
    if (folderId !== TRASH) acts.push(quick(t('soubory.open'), 'folder-open', () => void openItem(one)));
    if (one.kind === 'file' && !one.isShortcut && folderId !== TRASH) acts.push(quick(t('soubory.download'), 'download', () => void downloadItem(one, prefix, true)));
    const props = h('div', { class: 'files-detail-props' });
    const shares = h('div', { class: 'files-detail-shares' });
    const tagsEl = folderId !== TRASH ? detailTags(one) : null;
    detailEl.replaceChildren(card(previewOf(one), one.name, typeLabel(one) + (one.kind === 'file' ? ' · ' + fmtSize(one.size) : ''), acts), props, tagsEl ?? '', shares);
    const modified = (by: string | null): string => fmtDateTime(one.updatedAt ?? one.createdAt) + (by ? ' · ' + by : '');
    if (folderId === TRASH) {
      props.append(aboutRow(t('desktop.props.modified'), modified(null)));
      return;
    }
    try {
      const info = await core<Info>('storage', 'info', { id: one.id });
      if (seq !== detailSeq) return;
      props.append(
        aboutRow(t('desktop.props.location'), info.path.slice(0, -1).map((x, i) => (i === 0 ? rootName(x) : x.name)).join(' / ') || '/'),
        aboutRow(t('desktop.props.owner'), info.owner === 'shared' ? t('soubory.shared') : info.owner ?? '—'),
        aboutRow(t('desktop.props.access'), info.access ? t('desktop.props.access_' + info.access) : '—'),
        aboutRow(t('desktop.props.created'), fmtDateTime(one.createdAt) + (info.createdBy ? ' · ' + info.createdBy : '')),
      );
      if (one.updatedAt) props.append(aboutRow(t('desktop.props.modified'), modified(info.updatedBy)));
      if (sharable(one)) detailEl.querySelector('.record-card-acts')?.appendChild(quick(t('soubory.share'), 'users', () => shareDialog(one)));
      if ((one.space === 'user' || one.space === 'shared') && canWrite(one.access)) {
        void core<{ links: { state: string; downloads: number; uploads: number }[] }>('links', 'forItem', { id: one.id }).then((r) => {
          const act = r.links.filter((l) => l.state === 'active');
          if (seq !== detailSeq || !act.length) return;
          const btn = h('button', { type: 'button', class: 'btn sm' }, icon('link'), t('soubory.links_open'));
          btn.addEventListener('click', () => void wm.open({ app: 'sdileni', window: 'odkazy' }));
          shares.append(h('div', { class: 'files-detail-sec' }, t('soubory.links_section')), h('p', { class: 'muted' }, t('soubory.links_count', { count: act.length, down: act.reduce((n, l) => n + l.downloads, 0), up: act.reduce((n, l) => n + l.uploads, 0) })), btn);
        }).catch(() => undefined);
      }
      if (info.canShare) {
        shares.append(h('div', { class: 'files-detail-sec' }, t('soubory.shared_with')), info.shares.length ? h('ul', null, info.shares.map((s) => h('li', null, h('span', null, s.name), h('span', { class: 'muted' }, t('core.share.' + s.level))))) : h('p', { class: 'muted' }, t('soubory.not_shared')));
      }
    } catch {
      if (seq === detailSeq) props.append(aboutRow(t('desktop.props.modified'), modified(null)));
    }
  }

  /** Oddíl Štítky v podokně: barevné čipy (klik = vše s tímto štítkem) a tlačítko pro úpravu. */
  function detailTags(one: StorageItem): HTMLElement | null {
    const tags = one.tags ?? [];
    const can = taggable([one]);
    if (!tags.length && !can) return null;
    const chips = tags.map((n) => {
      const c = h('button', { type: 'button', class: 'tag-chip colored files-tag-chip', style: { '--tag': labelColor(labelList, n) ?? '#888' }, title: t('core.labels.cmd_tagged') }, n);
      const l = labelList.find((x) => x.name.toLowerCase() === n.toLowerCase());
      c.addEventListener('click', () => void wm.open({ app: 'desktop', window: 'oznacene', params: l ? { label: l.id } : {} }));
      return c;
    });
    const out = h('div', { class: 'files-detail-tags' }, h('div', { class: 'files-detail-sec' }, t('soubory.labels')), h('div', { class: 'tags-chips' }, chips.length ? chips : h('span', { class: 'muted' }, t('soubory.no_labels'))));
    if (can) {
      const btn = h('button', { type: 'button', class: 'btn sm' }, icon('tag'), t('soubory.labels_edit'));
      btn.addEventListener('click', () => { IwMenu.showAt(tagMenu([one]), btn); });
      out.append(btn);
    }
    return out;
  }

  function renderStatus(): void {
    const hint = bar && selected.size === 1 && wm.currentMode() !== 'mobile' ? h('span', { class: 'muted' }, t('core.ui.open_hint')) : '';
    status.replaceChildren(statusBar(t('soubory.items', { count: bar && searchTerm ? shownItems().length : items.length }), selected.size ? t('core.ui.selected', { count: selected.size }) : '', folderAccess === 'read' ? t('soubory.read_only') : hint));
  }

  function itemMenu(it: StorageItem): MenuItem[] {
    const sel = selectedItems();
    if (folderId === TRASH) {
      return [
        { label: t('soubory.restore'), icon: 'rotate', onSelect: () => void restore(sel) },
        { label: t('soubory.purge'), icon: 'trash', danger: true, onSelect: () => void purge(sel) },
      ];
    }
    const m: MenuItem[] = [{ label: t('soubory.open'), icon: 'folder-open', onSelect: () => void openItem(it) }];
    if (it.kind === 'file' && !it.isShortcut) m.push({ label: t('soubory.download'), icon: 'download', onSelect: () => void downloadItem(it, prefix, true) });
    if (folderId === SHARED_ROOT) {
      m.push({ separator: true, label: '' });
      m.push({ label: t('soubory.remove_shared'), icon: 'x', danger: true, onSelect: () => void removeShared(sel) });
      m.push({ label: t('soubory.properties'), icon: 'info', onSelect: () => void wm.open({ app: 'desktop', window: 'vlastnosti', params: { id: it.id }, modal: true, parent: win }) });
      return m;
    }
    const w = sel.every((i) => canWrite(i.access));
    m.push({ separator: true, label: '' });
    m.push({ label: t('soubory.cut'), icon: 'scissors', shortcut: 'Ctrl+X', disabled: !w, onSelect: () => setFileClipboard(sel.map((i) => i.id), true) });
    m.push({ label: t('soubory.copy'), icon: 'copy', shortcut: 'Ctrl+C', onSelect: () => setFileClipboard(sel.map((i) => i.id), false) });
    m.push({ label: t('soubory.move_to'), icon: 'folder-open', disabled: !w, onSelect: () => void moveTo(sel) });
    m.push({ label: t('soubory.rename'), icon: 'edit', shortcut: 'F2', disabled: sel.length !== 1 || !w, onSelect: () => void rename(it) });
    m.push({ label: t('soubory.delete'), icon: 'trash', shortcut: 'Delete', danger: true, disabled: !w, onSelect: () => void trash(sel) });
    m.push({ separator: true, label: '' });
    if (sharable(it)) m.push({ label: t('soubory.share'), icon: 'users', onSelect: () => shareDialog(it) });
    if (taggable(sel)) m.push({ label: t('soubory.labels'), icon: 'tag', children: tagMenu(sel) });
    m.push({ label: t('soubory.properties'), icon: 'info', onSelect: () => void wm.open({ app: 'desktop', window: 'vlastnosti', params: { id: it.id }, modal: true, parent: win }) });
    return bar ? tidy([...m, { separator: true, label: '' }, { label: t('core.cmd.select_all'), icon: 'check-square', shortcut: 'Ctrl+A', onSelect: () => selectAll() }, { label: t('core.cmd.refresh'), icon: 'refresh', shortcut: 'F5', onSelect: () => void load() }]) : m;
  }
  /** Nové ovládání: co pro výběr nejde, v nabídce není (jako seznamy jádra); zdvojené oddělovače pryč. */
  function tidy(items: MenuItem[]): MenuItem[] {
    const out: MenuItem[] = [];
    for (const it of items.filter((x) => !x.disabled)) {
      if (it.separator && (!out.length || out[out.length - 1]?.separator)) continue;
      out.push(it);
    }
    while (out[out.length - 1]?.separator) out.pop();
    return out;
  }

  /** Přesunout do… – výběr cílové složky dialogem (na dotyku náhrada za přetažení). */
  async function moveTo(items: StorageItem[]): Promise<void> {
    const target = await pickFolder({ parent: win });
    if (!target || !items.length) return;
    try {
      await core('storage', 'move', { ids: items.map((i) => i.id), target: target.id });
      toast(t('soubory.moved_to', { name: target.name }), 'ok');
      void load();
    } catch (e) {
      toast(tk((e as ApiError).message, (e as ApiError).params ?? undefined), 'error');
    }
  }
  function folderMenu(): MenuItem[] {
    if (folderId === TRASH) return [{ label: t('soubory.empty_trash_cmd'), icon: 'trash', danger: true, onSelect: () => void emptyTrash() }];
    if (folderId === SHARED_ROOT) return [{ label: t('core.cmd.refresh'), icon: 'refresh', shortcut: 'F5', onSelect: () => void load() }];
    const writable = canWrite(folderAccess);
    return [
      { label: t('desktop.shell.new'), icon: 'plus', children: [
        { label: t('soubory.new_folder'), icon: 'folder', disabled: !writable, onSelect: () => void newFolder() },
        { label: t('desktop.shell.new_text'), icon: 'file-text', disabled: !writable, onSelect: () => void newText() },
      ] },
      { label: t('desktop.shell.upload'), icon: 'upload', disabled: !writable, onSelect: () => pickUpload(false) },
      { label: t('soubory.upload_folder'), icon: 'folder', disabled: !writable, onSelect: () => pickUpload(true) },
      { label: t('soubory.paste'), icon: 'clipboard', shortcut: 'Ctrl+V', disabled: !fileClipboard() || !writable, onSelect: () => void paste() },
      { separator: true, label: '' },
      ...(bar ? [{ label: t('core.cmd.select_all'), icon: 'check-square', shortcut: 'Ctrl+A', onSelect: () => selectAll() }] : []),
      { label: t('core.cmd.refresh'), icon: 'refresh', shortcut: 'F5', onSelect: () => void load() },
    ];
  }
  contextMenu(listEl, (e) => ((e.target as HTMLElement).closest('[data-id]') ? null : bar ? tidy(folderMenu().map((x) => (x.children ? { ...x, children: tidy(x.children) } : x))) : folderMenu()));
  // klávesnice jako seznamy jádra: šipky, Home/End, Enter otevře, Esc zruší výběr
  listEl.addEventListener('keydown', (e) => {
    if (!bar || e.ctrlKey || e.metaKey || e.altKey) return;
    const list = shownItems();
    if (!list.length) return;
    let i = list.findIndex((x) => x.id === (cursorId ?? Array.from(selected).pop()));
    const cols = view === 'tiles' ? Math.max(1, Math.floor((listEl.querySelector('.tiles')?.clientWidth ?? 120) / 116)) : 1;
    const keys: Record<string, number> = { ArrowDown: cols, ArrowUp: -cols, ...(view === 'tiles' ? { ArrowRight: 1, ArrowLeft: -1 } : {}) };
    if (e.key in keys) i = Math.max(0, Math.min(list.length - 1, (i < 0 ? -1 : i) + (keys[e.key] ?? 0)));
    else if (e.key === 'PageDown' || e.key === 'PageUp') {
      const step = Math.max(1, Math.floor(listEl.clientHeight / (view === 'tiles' ? 120 : 36)) - 1) * cols;
      i = Math.max(0, Math.min(list.length - 1, (i < 0 ? 0 : i) + (e.key === 'PageDown' ? step : -step)));
    } else if (e.key === 'Home') i = 0;
    else if (e.key === 'End') i = list.length - 1;
    else if (e.key === 'Enter') { const one = selectedItems(); if (one.length === 1 && one[0]) { e.preventDefault(); void openItem(one[0]); } return; }
    else if (e.key === 'Escape' && selected.size) { e.preventDefault(); if (selectMode) setSelectMode(false); else clearSelection(); return; }
    else return;
    e.preventDefault();
    const it = list[i];
    if (!it) return;
    cursorId = it.id;
    if (e.shiftKey) selected.add(it.id);
    else selected = new Set([it.id]);
    paintSelection();
    refreshRibbon();
    renderStatus();
    scrollToItem(i);
    paintSelection();
  });
  listEl.addEventListener('click', (e) => {
    if (e.target === listEl || (e.target as HTMLElement).classList.contains('tiles')) {
      selected = new Set();
      paintSelection();
      refreshRibbon();
      renderStatus();
    }
  });

  // ---- akce
  async function openItem(it: StorageItem): Promise<void> {
    if (it.kind === 'folder') return navigate(it.id);
    if (it.isShortcut) {
      const r = await core<{ content: string }>('storage', 'text', { id: it.id });
      const sc = JSON.parse(r.content) as Shortcut;
      await wm.open({ app: sc.app, window: sc.window, params: sc.params ?? {} });
      return;
    }
    const shell = (window as unknown as { iwanDesktop?: { appForMime(m: string | null): { app: string; window: string } | null } }).iwanDesktop;
    const target = shell?.appForMime(it.mime) ?? (it.mime && (it.mime.startsWith('image/') || it.mime.startsWith('text/') || it.mime === 'application/pdf') ? { app: 'prohlizec', window: 'zobrazit' } : null);
    if (target) {
      // Prohlížeč dostane pořadí zobrazitelných souborů složky (listování šipkami / přejetím)
      const viewable = (x: StorageItem): boolean => x.kind === 'file' && !x.isShortcut && (shell?.appForMime(x.mime)?.app === 'prohlizec' || Boolean(x.mime && (x.mime.startsWith('image/') || x.mime.startsWith('text/') || x.mime === 'application/pdf' || x.mime === 'message/rfc822')));
      const restore = target.app === 'prohlizec' ? { nav: { ids: items.filter(viewable).map((x) => x.id) } } : undefined;
      await wm.open({ app: target.app, window: target.window, params: { id: it.id }, restore });
    } else void downloadItem(it, prefix);
  }
  async function newFolder(): Promise<void> {
    const name = await prompt(t('desktop.shell.folder_name'), t('soubory.new_folder'));
    if (name === null) return;
    await core('storage', 'mkdir', { parent: folderId, name }).catch((e: ApiError) => toastError(e));
    events.kick();
    void load();
  }
  async function newText(): Promise<void> {
    const name = await prompt(t('desktop.shell.file_name'), t('soubory.new_text'));
    if (name === null) return;
    const r = await core<{ item: StorageItem }>('storage', 'textfile', { parent: folderId, name, content: '', mime: 'text/plain' }).catch((e: ApiError) => {
      toastError(e);
      return null;
    });
    events.kick();
    void load();
    if (r) void wm.open({ app: 'prohlizec', window: 'zobrazit', params: { id: r.item.id } });
  }
  function pickUpload(folder: boolean): void {
    void pickNative({ folder, multiple: true }).then((c) => c && doUpload(c.files, c.paths));
  }
  async function doUpload(files: FileList | File[], paths?: string[], dirs?: string[]): Promise<void> {
    await uploadFiles(folderId, files, paths, false, dirs);
    events.kick();
    void load();
  }
  async function rename(it: StorageItem): Promise<void> {
    const name = await prompt(t('soubory.rename'), it.name);
    if (name === null || name === it.name) return;
    await core('storage', 'rename', { id: it.id, name }).catch((e: ApiError) => toastError(e));
    events.kick();
    void load();
  }
  async function trash(sel: StorageItem[]): Promise<void> {
    if (!sel.length) return;
    const ids = sel.map((i) => i.id);
    try {
      await core('storage', 'trash', { ids });
      // hláška jen po úspěchu, s „Zpět“ (obnovit z koše) – kontrola 3. 11.
      toastAction(t('soubory.deleted'), 'ok', { label: t('core.ui.undo'), onClick: () => void Promise.all(ids.map((id) => core('storage', 'restore', { id }))).then(() => { events.kick(); void load(); }).catch(toastError) });
    } catch (e) { toastError(e); }
    events.kick();
    void load();
  }
  async function removeShared(sel: StorageItem[]): Promise<void> {
    for (const it of sel) await core('sharing', 'remove', { type: 'storage', id: it.id, self: true }).catch((e: ApiError) => toastError(e));
    events.kick();
    void load();
  }
  async function restore(sel: StorageItem[]): Promise<void> {
    for (const it of sel) await core('storage', 'restore', { id: it.id }).catch((e: ApiError) => toastError(e));
    events.kick();
    void load();
  }
  async function purge(sel: StorageItem[]): Promise<void> {
    if (!sel.length || !(await confirm(t('soubory.confirm_purge'), { danger: true }))) return;
    let n = 0;
    for (const it of sel) await core('storage', 'purge', { id: it.id }).then(() => { n++; }).catch(toastError);
    if (n) toast(t('core.ui.deleted_n', { n }), 'ok');
    void load();
  }
  async function emptyTrash(): Promise<void> {
    if (!(await confirm(t('soubory.confirm_empty'), { danger: true }))) return;
    await core('storage', 'emptyTrash').catch((e: ApiError) => toastError(e));
    void load();
  }
  async function paste(): Promise<void> {
    if (folderId === TRASH) return;
    if (await pasteFileClipboard(folderId)) void load();
  }
  const offClip = onFileClipboard(() => refreshRibbon());
  async function dropOn(targetId: string, e: DragEvent): Promise<void> {
    e.preventDefault();
    const exp = e.dataTransfer?.getData('application/x-iwan-export');
    if (exp && targetId !== TRASH) { // jiná aplikace nabízí kopii (e-mail jako .eml): akce jejího okna s cílovou složkou
      await runExport(exp, targetId);
    } else if (targetId === TRASH) {
      const raw = e.dataTransfer?.getData('application/x-iwan-items');
      if (raw) await core('storage', 'trash', { ids: JSON.parse(raw) as string[] }).catch((er: ApiError) => toast(tk(er.message), 'error'));
    } else if (e.dataTransfer?.files.length) {
      const c = await collectFromDrop(e.dataTransfer);
      const saved = folderId;
      folderId = targetId;
      await doUpload(c.files, c.paths, c.dirs);
      folderId = saved;
    } else {
      const raw = e.dataTransfer?.getData('application/x-iwan-items');
      if (raw) {
        const ids = (JSON.parse(raw) as string[]).filter((id) => id !== targetId);
        if (ids.length) await core('storage', 'move', { ids, target: targetId }).catch((er: ApiError) => toast(tk(er.message), 'error'));
      }
    }
    events.kick();
    void load();
  }
  const fromOutside = (e: DragEvent): boolean => Boolean(e.dataTransfer?.types.includes('Files') || e.dataTransfer?.types.includes('application/x-iwan-export'));
  listEl.addEventListener('dragover', (e) => {
    if (fromOutside(e) && folderId !== TRASH) e.preventDefault();
  });
  dropHighlight(listEl, (e) => fromOutside(e) && folderId !== TRASH && !(e.target as HTMLElement).closest('[data-id]'));
  listEl.addEventListener('drop', (e) => {
    if ((e.target as HTMLElement).closest('[data-id]')?.classList.contains('drop')) return;
    const exp = e.dataTransfer?.getData('application/x-iwan-export');
    if (exp && folderId !== TRASH) { e.preventDefault(); void runExport(exp, folderId).then(() => events.kick()); return; }
    if (e.dataTransfer?.files.length && folderId !== TRASH) {
      e.preventDefault();
      void collectFromDrop(e.dataTransfer).then((c) => doUpload(c.files, c.paths, c.dirs));
    }
  });
  function setView(v: 'list' | 'tiles'): void {
    view = v;
    if (bar) void savePref('soubory.view', v);
    renderList();
    refreshRibbon();
  }

  void navigate(folderId, false);

  return {
    el,
    refresh: () => void load(),
    getState: () => ({ folder: folderId, view }),
    setState: (s) => {
      const st = s as { folder?: string; view?: 'list' | 'tiles' } | null;
      if (st?.view) view = st.view;
      if (st?.folder) void navigate(st.folder, false);
    },
    shortcut: (sc: string) => {
      const sel = selectedItems();
      if (sc === 'F2' && sel.length === 1 && sel[0]) {
        void rename(sel[0]);
        return true;
      }
      if (sc === 'Delete' && sel.length) {
        void (folderId === TRASH ? purge(sel) : trash(sel));
        return true;
      }
      if (sc === 'F5') {
        void load();
        return true;
      }
      if (sc === 'Ctrl+B') {
        setTree(!treeHidden);
        return true;
      }
      if (sc === 'Backspace' && path.length > 1) {
        goUp();
        return true;
      }
      if (sc === 'Ctrl+F') {
        if (hs) hs.focus();
        else {
          searchInput.focus();
          searchInput.select();
        }
        return true;
      }
      if (sc === 'Ctrl+X' && sel.length) {
        setFileClipboard(sel.map((i) => i.id), true);
        return true;
      }
      if (sc === 'Ctrl+C' && sel.length) {
        setFileClipboard(sel.map((i) => i.id), false);
        return true;
      }
      if (sc === 'Ctrl+V' && fileClipboard()) {
        void paste();
        return true;
      }
      if (sc === 'Ctrl+A') {
        selectAll();
        return true;
      }
      return false;
    },
    destroy: () => {
      offLabels();
      unsubscribe?.();
      layoutObserver.disconnect();
      offClip();
      IwMenu.closeAll();
    },
    setMode: (m: 'pc' | 'tablet' | 'mobile') => {
      if (bar) {
        if (m !== 'mobile' && selectMode && backSel) setSelectMode(false);
        renderHeader();
        renderStatus();
      } else ribbon.setMode(m);
    },
    focus: () => listEl.focus(),
  } as ReturnType<Parameters<typeof registerComponent>[1]> & { shortcut(sc: string): boolean };
});
